import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  ShieldCheck,
  AlertTriangle,
  EyeOff,
  Percent,
  Landmark,
  Handshake,
  MapPin,
  CheckCircle2,
} from "lucide-react";
import { BRAND, IMAGES, telLink, waLink } from "@/lib/brand";
import BrandStamp from "@/components/BrandStamp";

const AREAS = [
  "Gurgaon",
  "Manesar",
  "Delhi",
  "Faridabad",
  "Noida",
  "Ghaziabad",
  "Sohna",
  "Dharuhera",
  "Rewari",
  "Palwal",
  "Bhiwadi",
  "Neemrana",
];

const BENEFITS = [
  {
    icon: AlertTriangle,
    title: "Avoid 10–20% Auction Loss",
    hindi: "नीलामी में 10–20% नुकसान से बचें",
    body: "If you miss EMIs, the bank auctions your gold. The winning bidder buys it 10% below market plus pays GST on top — you effectively lose 10–20% of your gold's real value. Selling to Top Rate first stops this.",
  },
  {
    icon: EyeOff,
    title: "Complete Discretion",
    hindi: "पूर्ण गोपनीयता",
    body: "When a bank auctions pledged gold, your name, address and loan default are published in newspapers and legal notices. Everyone in your circle can read it. We handle everything privately — no public notice, ever.",
  },
  {
    icon: Percent,
    title: "Commission as low as 2%",
    hindi: "सिर्फ 2% कमीशन",
    body: "No hidden charges, no processing fees, no 'weighing loss' games. Just a flat, transparent 2% commission on the transaction — one of the lowest in North India.",
  },
  {
    icon: Landmark,
    title: "We Settle Directly with Bank",
    hindi: "बैंक से सीधे सेटलमेंट",
    body: "SBI, HDFC, Muthoot, Manappuram, IIFL, Federal Bank, Canara Bank — we deal end-to-end with your lender. You don't have to arrange a rupee upfront. Loan closure paperwork is handled by us.",
  },
  {
    icon: Handshake,
    title: "EMI Burden Gone in 24 Hours",
    hindi: "24 घंटे में EMI का बोझ ख़त्म",
    body: "One meeting, one signature, one settlement. Balance after loan payoff is transferred to your bank account or paid in cash — same day.",
  },
  {
    icon: ShieldCheck,
    title: "Govt-Approved Valuers",
    hindi: "सरकारी अनुमोदित मूल्यांकनकर्ता",
    body: "Officially recognised valuers — the same certificates are accepted by banks and courts. Every step of karat testing and weighing happens in front of you.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Call / WhatsApp Us",
    body: `Send us your bank name, outstanding loan amount and approximate gold weight on ${BRAND.phone}.`,
  },
  {
    n: "02",
    title: "Free On-Call Estimate",
    body: "We give you an indicative payout after loan settlement — before you commit to anything.",
  },
  {
    n: "03",
    title: "Bank Coordination",
    body: "We coordinate with your bank for gold release, no-dues certificate and pledge cancellation.",
  },
  {
    n: "04",
    title: "Payout Same Day",
    body: "Loan cleared with the bank; balance credited to you in cash or bank transfer within hours.",
  },
];

const ContactStrip = () => (
  <div
    data-testid="mortgage-contact-strip"
    className="bg-[#641C24] text-[#FFFDF8] border-y border-[#D4AF37]/40"
  >
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="inline-flex items-center justify-center w-9 h-9 bg-[#D4AF37] text-[#2D181A]">
          <Phone size={16} strokeWidth={2.2} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Talk to us · अभी बात करें
          </div>
          <a
            href={telLink}
            data-testid="mortgage-phone-top"
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#FFFDF8] hover:text-[#D4AF37] transition-colors"
          >
            {BRAND.phone}
          </a>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={waLink(
            "Namaste! Mera gold bank mein girvi hai. Kripya rescue karne ke process aur best rate ke baare mein bataye. (Top Rate)"
          )}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mortgage-whatsapp-top"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        >
          <MessageCircle size={15} /> WhatsApp
        </a>
        <a
          href={telLink}
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8860B] text-[#2D181A] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        >
          <Phone size={15} /> Call Now
        </a>
      </div>
    </div>
  </div>
);

export default function MortgagedGoldPage() {
  return (
    <div>
      <ContactStrip />

      {/* HERO */}
      <section
        data-testid="mortgage-hero"
        className="relative overflow-hidden bg-[#FAF6EC]"
      >
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-25 hidden md:block"
          loading="eager"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,253,248,1) 0%, rgba(255,253,248,0.95) 45%, rgba(255,253,248,0.35) 75%, rgba(255,253,248,0) 100%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Copy */}
            <div className="lg:col-span-7 tr-fade-up">
              <div className="inline-flex items-center gap-2 border border-[#641C24]/40 bg-[#FFFDF8] text-[#641C24] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold">
                <Landmark size={13} strokeWidth={2} />
                Mortgaged Gold Buyback · गिरवी सोना
              </div>

              <h1 className="font-display text-[#2D181A] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.98] mt-6">
                <span className="italic text-[#B8860B]">Apna Girvi Sona</span>
                <br />
                <span>Bank se Chudwaye.</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-[#4A3B3C] leading-relaxed max-w-2xl">
                Gold pledged with a bank or NBFC and drowning in EMIs? Top
                Rate buys your mortgaged gold at the best market rate and
                settles the loan directly with your lender. Discreet,
                transparent, quick.
              </p>

              <ul className="mt-6 space-y-3 text-sm sm:text-base text-[#2D181A]">
                <li className="flex items-start gap-3">
                  <CheckCircle2
                    size={18}
                    className="mt-0.5 text-[#641C24] shrink-0"
                  />
                  <span>
                    <span className="font-semibold">We help you get rid of EMIs</span>{" "}
                    — the moment we buy your gold, the loan is closed.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2
                    size={18}
                    className="mt-0.5 text-[#641C24] shrink-0"
                  />
                  <span>
                    <span className="font-semibold">
                      Commission as low as 2%
                    </span>{" "}
                    — no hidden charges, no “melting loss” tricks.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2
                    size={18}
                    className="mt-0.5 text-[#641C24] shrink-0"
                  />
                  <span>
                    <span className="font-semibold">100% discreet</span> — no
                    newspaper notices, no public embarrassment.
                  </span>
                </li>
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={waLink(
                    "Namaste! Mera gold bank mein girvi hai. Kripya rescue karne ke process aur best rate ke baare mein bataye. (Top Rate)"
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mortgage-hero-whatsapp"
                  className="inline-flex items-center justify-center gap-2 bg-[#641C24] hover:bg-[#4A151A] text-[#FFFDF8] px-7 py-4 text-sm font-semibold uppercase tracking-wider"
                >
                  <MessageCircle size={17} /> Free Consultation on WhatsApp
                </a>
                <a
                  href={telLink}
                  data-testid="mortgage-hero-call"
                  className="inline-flex items-center justify-center gap-2 border border-[#641C24]/60 hover:bg-[#641C24] hover:text-[#FFFDF8] text-[#641C24] px-7 py-4 text-sm font-semibold uppercase tracking-wider"
                >
                  <Phone size={17} /> Call {BRAND.phone}
                </a>
              </div>
            </div>

            {/* Visual: Gold + ₹500 notes composition */}
            <div className="lg:col-span-5 relative flex items-center justify-center">
              <div className="relative w-full max-w-md">
                <img
                  src={IMAGES.ornament1}
                  alt="Indian gold ornaments — pledged gold rescue at Top Rate"
                  loading="eager"
                  className="w-full h-72 md:h-80 object-cover shadow-[0_18px_44px_-14px_rgba(45,24,26,0.4)]"
                />
                <img
                  src={IMAGES.rupees500}
                  alt="₹500 notes — cash payout after bank settlement"
                  loading="eager"
                  className="absolute -bottom-8 -right-4 w-3/5 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.5)] rotate-[6deg]"
                />
                <img
                  src={IMAGES.rupees500}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute -bottom-4 -left-3 w-2/5 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.4)] -rotate-[6deg] hidden sm:block"
                />
                <div className="absolute -top-5 -left-3 bg-[#641C24] text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Loan Closed ✓
                </div>
                <div className="absolute top-4 -right-3 bg-[#D4AF37] text-[#2D181A] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Only 2% Commission
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={
          <>
            We Rescue Pledged Gold Across Gurgaon, Manesar &amp; Delhi NCR
          </>
        }
      />

      {/* WHY IT MATTERS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              Why Rescue Your Gold Now
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
              Waiting for auction =
              <span className="italic text-[#641C24]"> guaranteed loss</span>.
            </h2>
          </div>
          <div className="lg:col-span-4 text-[#4A3B3C] text-base leading-relaxed">
            Two brutal realities of bank auctions that most people don't
            realise until it's too late.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          <div className="bg-[#641C24] text-[#FFFDF8] p-8 md:p-10 border-l-4 border-[#D4AF37]">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              <AlertTriangle size={14} /> Problem #1
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-4 leading-tight">
              You lose 10–20% of your gold's value.
            </h3>
            <p className="text-[#FFFDF8]/85 leading-relaxed mt-4">
              Banks auction pledged gold at{" "}
              <span className="text-[#D4AF37] font-semibold">
                at least 10% below market rate
              </span>{" "}
              — plus the auction purchaser pays{" "}
              <span className="text-[#D4AF37] font-semibold">GST on top</span>.
              That entire cost cascade eats into what you finally recover.
              End result: <span className="font-semibold">10–20% of your gold's real value is gone</span>{" "}
              — permanently.
            </p>
          </div>

          <div className="bg-[#2D181A] text-[#FFFDF8] p-8 md:p-10 border-l-4 border-[#D4AF37]">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              <EyeOff size={14} /> Problem #2
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-4 leading-tight">
              Your name gets published publicly.
            </h3>
            <p className="text-[#FFFDF8]/85 leading-relaxed mt-4">
              Before an auction, banks are{" "}
              <span className="text-[#D4AF37] font-semibold">
                required to publish borrower details in newspapers
              </span>{" "}
              and legal notices. Your name, address and default become public
              record — visible to family, friends and neighbours. With Top
              Rate, the entire settlement is{" "}
              <span className="font-semibold">100% discreet and private</span>.
            </p>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={
          <>
            Discreet · Transparent · Best Rate — This Is Top Rate's Promise
          </>
        }
      />

      {/* BENEFITS GRID */}
      <section className="bg-[#FAF6EC] tr-grain border-y border-[#E5DCC5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
            <div className="lg:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
                Benefits · लाभ
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
                Six reasons families in Gurgaon &amp; Delhi{" "}
                <span className="italic text-[#641C24]">choose Top Rate</span>{" "}
                for mortgage buyback.
              </h2>
            </div>
            <div className="lg:col-span-4 text-[#4A3B3C] text-base leading-relaxed">
              We buy directly from you, settle with the bank, close the loan,
              and hand you the balance — all in one visit.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {BENEFITS.map((b, i) => (
              <div
                key={b.title}
                data-testid={`mortgage-benefit-${i}`}
                className="bg-white border border-[#E5DCC5] p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-11 h-11 border border-[#D4AF37] flex items-center justify-center text-[#641C24]">
                  <b.icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl font-bold mt-5">
                  {b.title}
                </h3>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#641C24] mt-1">
                  {b.hindi}
                </div>
                <p className="text-sm text-[#4A3B3C] leading-relaxed mt-4">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandStamp
        variant="light"
        tagline={<>Trusted for Discreet Gold Loan Settlement · No Auction Loss</>}
      />

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
          How It Works · कैसे काम करता है
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
          <span className="italic text-[#641C24]">Four simple steps.</span> Loan
          gone. Balance in your hand.
        </h2>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="bg-white border border-[#E5DCC5] p-8 shadow-sm relative"
            >
              <div className="font-display text-6xl font-black text-[#D4AF37]/25 leading-none">
                {s.n}
              </div>
              <h3 className="font-display text-xl font-bold mt-3">
                {s.title}
              </h3>
              <p className="text-sm text-[#4A3B3C] leading-relaxed mt-3">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={<>Serving Gurgaon · Manesar · Delhi &amp; All Nearby Areas</>}
      />

      {/* AREAS + CONTACT */}
      <section className="bg-[#2D181A] text-[#FFFDF8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                Coverage Areas · सेवा क्षेत्र
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
                Home visits across{" "}
                <span className="italic text-[#D4AF37]">
                  Gurgaon, Manesar, Delhi
                </span>{" "}
                &amp; nearby areas.
              </h2>
              <p className="text-[#FFFDF8]/80 leading-relaxed mt-6 max-w-xl">
                We travel to your home, your lender's branch, or wherever the
                gold is stored — with certified valuers, calibrated scales and
                XRF karat tester. Complete privacy guaranteed.
              </p>

              <div className="flex flex-wrap gap-2 mt-8">
                {AREAS.map((a) => (
                  <span
                    key={a}
                    className="inline-flex items-center gap-1.5 border border-[#D4AF37]/60 text-[#D4AF37] px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold"
                  >
                    <MapPin size={11} /> {a}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-[#FFFDF8] text-[#2D181A] p-8 md:p-10 shadow-2xl">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
                  Contact Us Today · अभी संपर्क करें
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight">
                  Rescue your gold before the auction notice arrives.
                </h3>

                <div className="mt-6 space-y-3">
                  <a
                    href={telLink}
                    data-testid="mortgage-cta-call"
                    className="flex items-center justify-between gap-3 bg-[#641C24] hover:bg-[#4A151A] text-[#FFFDF8] px-6 py-4 transition-colors"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Phone size={16} strokeWidth={2.2} />
                      <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                        Call
                      </span>
                    </span>
                    <span className="font-display font-bold text-lg tracking-tight">
                      {BRAND.phone}
                    </span>
                  </a>
                  <a
                    href={waLink(
                      "Namaste! Mera gold bank mein girvi hai. Kripya rescue karne ke process aur best rate ke baare mein bataye. (Top Rate)"
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mortgage-cta-whatsapp"
                    className="flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1FBA57] text-white px-6 py-4"
                  >
                    <span className="inline-flex items-center gap-2">
                      <MessageCircle size={16} strokeWidth={2.2} />
                      <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                        WhatsApp
                      </span>
                    </span>
                    <span className="font-display font-bold text-lg tracking-tight">
                      Chat Now
                    </span>
                  </a>
                </div>

                <div className="mt-6 border-t border-[#E5DCC5] pt-5 text-xs text-[#4A3B3C] leading-relaxed">
                  100% confidential. Free consultation. No commitment. Speak
                  in English, Hindi or Punjabi with our senior valuer.
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/bank-mortgaged-jewellery"
                    data-testid="mortgage-link-bank-jewellery"
                    className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#641C24] hover:text-[#B8860B]"
                  >
                    → Bank Mortgaged Jewellery Buy Back
                  </Link>
                  <Link
                    to="/branches"
                    className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#641C24] hover:text-[#B8860B]"
                  >
                    → Visit a Branch
                  </Link>
                  <Link
                    to="/about"
                    className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#641C24] hover:text-[#B8860B]"
                  >
                    → About Top Rate
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={
          <>Top Rate · Sona Do Paisa Lo · The Most Discreet Address in North India</>
        }
      />
    </div>
  );
}
